import { useState, useCallback, useEffect } from 'react';
//import { useHistory } from 'react-router-dom';
import * as jose from 'jose'
import config from '../config';

const coerceString = (value) => (typeof value === 'string' ? value.trim() : '');

const extractClaims = (decoded) => {
  if (!decoded || typeof decoded !== 'object') {
    return { admin: false, orgs: [] };
  }
  const admin = decoded.admin === true;
  const orgs = Array.isArray(decoded.orgs)
    ? decoded.orgs
        .map((org) => {
          const numeric = Number(org);
          return Number.isFinite(numeric) ? numeric : null;
        })
        .filter((value) => value !== null)
    : [];
  return { admin, orgs };
};

const buildUserInfo = (decoded) => {
  if (!decoded || typeof decoded !== 'object') {
    return null;
  }
  const rawInfo = decoded.userInfo && typeof decoded.userInfo === 'object' ? decoded.userInfo : {};
  const username = coerceString(decoded.username || rawInfo.username || '');
  const email = coerceString(decoded.email || rawInfo.email || '') || username;
  const firstName = coerceString(decoded.firstName || rawInfo.firstName || '');
  const lastName = coerceString(decoded.lastName || rawInfo.lastName || '');
  const nameFromParts = [firstName, lastName].filter(Boolean).join(' ').trim();
  const name = coerceString(decoded.name || rawInfo.name || '') || nameFromParts;

  const info = {
    username: username || null,
    email: email || null,
    firstName: firstName || null,
    lastName: lastName || null,
    name: name || null
  };

  return Object.values(info).some(Boolean) ? info : null;
};

const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [claims, setClaims] = useState({ admin: false, orgs: [] });
  const [userInfo, setUserInfo] = useState(null);
  const [tokenPayload, setTokenPayload] = useState(null);
  //const history = useHistory();

  const applyTokenDetails = useCallback((decoded) => {
    setTokenPayload(decoded || null);
    const parsedClaims = extractClaims(decoded);
    setClaims(parsedClaims);
    setUserInfo(buildUserInfo(decoded));
  }, []);

  const validateToken = useCallback(async (token) => {
    const currentTime = Date.now() / 1000;
    const canUseCrypto = typeof window !== 'undefined' && window.isSecureContext && window.crypto?.subtle;
    const isDev = (config.env || '').toLowerCase() === 'development';

    if (!canUseCrypto && isDev) {
      try {
        const decoded = jose.decodeJwt(token);
        return decoded?.exp ? decoded.exp > currentTime : true;
      } catch (error) {
        console.warn('Failed to decode JWT in insecure dev context', error);
        return false;
      }
    }

    try {
      const keyParsed = config.publicKey.replace(/\\n/g, '\n');
      // console.log('keyParsed', keyParsed);
      const publicKey = await jose.importSPKI(keyParsed, 'RS256');
      const decoded = await jose.jwtVerify(token, publicKey, { algorithms: ['RS256'] });
      return decoded?.payload?.exp > currentTime;
    } catch (error) {
      if (error.message === '"exp" claim timestamp check failed') {
        window.location.href=`${config.prefixPath}/login?externalError=expired`;
      }
      // console.log('====msg', error.message)
      // console.log(error)
      return false;
    }
  }, []);

  const effect = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token) {
      const isValid = await validateToken(token);
      if (isValid) {
        setIsAuthenticated(isValid);
        const decoded = jose.decodeJwt(token);
        applyTokenDetails(decoded);
      } else {
        localStorage.removeItem('token');
        setClaims({ admin: false, orgs: [] });
        setUserInfo(null);
        setTokenPayload(null);
      }
    }
    setIsAuthReady(true);
  }, [applyTokenDetails, validateToken]);


  useEffect(() => {
   effect();
  }, [effect]);

  const login = async (token) => {
    const valid = await validateToken(token);
    //console.log('valid', valid);
    if (valid) {
      localStorage.setItem('token', token);
      setIsAuthenticated(true);
      const decoded = jose.decodeJwt(token);
      applyTokenDetails(decoded);
      //console.log('Valid token');
      return true;
    } else {
      return false;
      //console.error('Invalid tokennnnn');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setClaims({ admin: false, orgs: [] });
    setUserInfo(null);
    setTokenPayload(null);
  };

  const canAdminOrganization = (orgId) => {
    if (!orgId && orgId !== 0) {
      return false;
    }
    if (claims.admin) {
      return true;
    }
    const numericOrgId = Number(orgId);
    if (!Number.isFinite(numericOrgId)) {
      return false;
    }
    return claims.orgs.includes(numericOrgId);
  };

  return {
    isAuthenticated,
    isAuthReady,
    isSiteAdmin: claims.admin,
    adminOrganizations: claims.orgs,
    userInfo,
    tokenPayload,
    canAdminOrganization,
    login,
    logout,
  };
};

export default useAuth;
